import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './MedCurationLanding.css';
import {
  completeHuggingFaceAuth,
  getHuggingFaceRedirectUri,
  HUGGING_FACE_RETURN_PATH_KEY,
  persistLabelBricksToken,
} from './medCurationAuth';

type CallbackState =
  | { kind: 'loading' }
  | { kind: 'error'; title: string; message: string };

// Guard against React StrictMode double-invocation firing the exchange twice.
const completionRequests = new Map<string, Promise<unknown>>();

function isSafeReturnPath(value: string | null): value is string {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.startsWith('/auth/huggingface/callback')
  );
}

function MedCurationCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<CallbackState>({ kind: 'loading' });

  useEffect(() => {
    const run = async () => {
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const code = searchParams.get('code');
      const oauthState = searchParams.get('state') || undefined;

      if (error) {
        setState({
          kind: 'error',
          title:
            error === 'access_denied'
              ? 'Sign-in was cancelled'
              : 'Hugging Face sign-in could not finish',
          message:
            errorDescription ||
            'Hugging Face returned an error before the sign-in could complete.',
        });
        return;
      }

      if (!code) {
        setState({
          kind: 'error',
          title: 'Missing authorization code',
          message:
            'Hugging Face did not include the code needed to finish signing in. Try again, and verify the OAuth redirect URL is configured correctly.',
        });
        return;
      }

      try {
        const redirectUri = getHuggingFaceRedirectUri();
        const requestKey = `${code}::${oauthState || ''}::${redirectUri}`;

        let request = completionRequests.get(requestKey);
        if (!request) {
          request = completeHuggingFaceAuth({
            code,
            state: oauthState,
            redirectUri,
          });
          completionRequests.set(requestKey, request);
        }

        const result = (await request) as Awaited<
          ReturnType<typeof completeHuggingFaceAuth>
        >;

        if (!result.token) {
          setState({
            kind: 'error',
            title: 'Sign-in could not create a session',
            message:
              result.message ||
              'Hugging Face approved the sign-in, but no session token was returned. Please try again.',
          });
          return;
        }

        persistLabelBricksToken(result.token);

        let returnPath = '/';
        try {
          const stored = localStorage.getItem(HUGGING_FACE_RETURN_PATH_KEY);
          if (isSafeReturnPath(stored)) {
            returnPath = stored;
          }
          localStorage.removeItem(HUGGING_FACE_RETURN_PATH_KEY);
        } catch {
          /* ignore storage failures */
        }

        navigate(returnPath, { replace: true });
      } catch (err) {
        setState({
          kind: 'error',
          title: 'Could not reach the sign-in service',
          message:
            err instanceof Error
              ? err.message
              : 'Failed to complete Hugging Face sign-in.',
        });
      }
    };

    void run();
  }, [navigate, searchParams]);

  return (
    <div className="mc-page">
      <div className="mc-callback">
        {state.kind === 'loading' ? (
          <div className="mc-callback-card">
            <span
              className="mc-spinner"
              aria-hidden="true"
            />
            <h1 className="mc-callback-title">Finishing sign-in…</h1>
            <p className="mc-callback-copy">
              Completing your Hugging Face session and opening Med-Curation.
            </p>
          </div>
        ) : (
          <div className="mc-callback-card">
            <h1 className="mc-callback-title">{state.title}</h1>
            <p className="mc-callback-copy">{state.message}</p>
            <button
              type="button"
              className="mc-btn mc-btn-hf"
              onClick={() => navigate('/welcome', { replace: true })}
            >
              Back to sign-in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default MedCurationCallback;
